import { createContext, useContext, useState } from "react";
import { X } from "lucide-react";
import { fmtMoney, lineAmount, applyDiscount, fmtDiscount, sectionAmounts, isVatFree } from "../lib/api";
import { useTr } from "../lib/publicI18n";

function safeImgSrc(src) {
  if (!src || typeof src !== 'string') return null;
  if (src.startsWith('https://') || src.startsWith('/api/files/')) return src;
  return null; // bloquer data:, javascript:, http:, chemins relatifs inconnus
}

/**
 * Image responsive + agrandissable (lightbox). Clic → overlay plein écran, clic/✕ pour fermer.
 * Réutilisée par les sections tarification et offre ajustable.
 */
export function ZoomableImage({ src, alt = "", className = "" }) {
  const tr = useTr();
  const [open, setOpen] = useState(false);
  const safe = safeImgSrc(src);
  if (!safe) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} aria-label={tr("Agrandir l'image", "Enlarge image")}
        className={`group block w-full cursor-zoom-in ${className}`}>
        <img src={safe} alt={alt} loading="lazy"
          className="w-full max-h-[440px] object-cover rounded-sm border border-border transition group-hover:brightness-95" />
      </button>
      {open && (
        <div role="dialog" aria-modal="true" onClick={() => setOpen(false)}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm cursor-zoom-out">
          <img src={safe} alt={alt} className="max-w-full max-h-full object-contain rounded-sm shadow-2xl" />
          <button type="button" onClick={() => setOpen(false)} aria-label={tr("Fermer", "Close")}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
    </>
  );
}

export function videoEmbedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  const loom = url.match(/loom\.com\/share\/([\w]+)/);
  if (loom) return `https://www.loom.com/embed/${loom[1]}`;
  return null;
}

/**
 * Style de la page client choisi par la société : « classic » (défaut, aussi dans l'éditeur) ou « premium »
 * (registre « conciergerie » : cartes blanches sur fond pierre). Fourni par PublicProposal ; lu par les sections et
 * les titres. En premium, PublicProposal pose la carte de chaque section, sauf la grille tarifaire qui dessine la sienne.
 */
export const ProposalThemeContext = createContext("classic");
const usePremium = () => useContext(ProposalThemeContext) === "premium";

export function SectionHeading({ children }) {
  if (usePremium()) {
    return <h2 className="font-display font-extrabold text-[1.55rem] sm:text-[1.85rem] leading-[1.08] tracking-[-0.02em] mb-4">{children}</h2>;
  }
  return (
    <div className="flex items-center gap-3 mb-6">
      <span className="w-2.5 h-2.5 bg-primary rotate-45 shrink-0" />
      <h2 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight">{children}</h2>
    </div>
  );
}

const STAT_COLS = { 1: "sm:grid-cols-1", 2: "sm:grid-cols-2", 3: "sm:grid-cols-3", 4: "sm:grid-cols-4" };

export default function SectionView({ section: s, currency = "EUR", clientName, brand }) {
  const items = s.items || [];
  const premium = usePremium();
  const tr = useTr();
  // Pas de TVA (taux effectif 0, ex. Saint-Barthélemy) : prix nets, sans mention HT.
  const HT = isVatFree(brand?.vat_rate) ? "" : tr("HT", "excl. VAT");
  const words = (...parts) => parts.filter(Boolean).join(" ");
  const commitmentText = (it) => (Number(it.commitment_months) > 0
    ? tr(`Engagement ${it.commitment_months} mois`, `${it.commitment_months}-month commitment`)
    : tr("Sans engagement", "No commitment"));

  if (s.type === "cover") {
    return (
      <div className={premium ? "text-center py-4" : "text-center py-4 sm:py-8 border-b border-border"}>
        <p className={premium ? "text-[12px] font-bold uppercase tracking-[0.06em] text-muted-foreground mb-3" : "text-xs tracking-[0.25em] uppercase font-semibold text-primary mb-4"}>{tr("Proposition commerciale", "Commercial proposal")}</p>
        <h1 className={premium ? "font-display font-extrabold text-4xl lg:text-5xl tracking-[-0.025em] leading-[1.05]" : "font-heading text-4xl lg:text-5xl font-bold tracking-tight leading-none"}>{s.title}</h1>
        {s.content && <p className="text-muted-foreground mt-5 max-w-xl mx-auto text-lg leading-relaxed">{s.content}</p>}
        {clientName && <p className="text-sm text-muted-foreground mt-6">{tr("Préparée pour", "Prepared for")} <span className="font-medium text-foreground">{clientName}</span></p>}
      </div>
    );
  }

  if (s.type === "text") {
    return (
      <div>
        <SectionHeading>{s.title}</SectionHeading>
        <p className={premium ? "text-foreground/80 leading-[1.75] whitespace-pre-line text-[15.5px]" : "text-foreground/80 leading-relaxed whitespace-pre-line text-[15px]"}>{s.content}</p>
        {/* Texte légal d'un client anglophone (récapitulatif contractuel) : la version française, qui fait foi, suit. */}
        {s.content_fr && (
          <div className="mt-6 pt-5 border-t border-border" lang="fr" data-testid={`section-fr-${s.id}`}>
            <p className="text-sm font-semibold mb-2">{tr("Version française (fait foi)", "French version (governing)")}</p>
            <p className="text-foreground/80 leading-relaxed whitespace-pre-line text-[15px]">{s.content_fr}</p>
          </div>
        )}
      </div>
    );
  }

  if (!premium && (s.type === "goals" || s.type === "process")) {
    return (
      <div>
        <SectionHeading>{s.title}</SectionHeading>
        <div className="space-y-6">
          {items.map((it, i) => (
            <div key={it.id || i} className="flex gap-5">
              <span className="font-heading text-2xl font-bold text-primary/30 tabular-nums shrink-0 w-10">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <h3 className="font-heading text-lg font-semibold">{it.title}</h3>
                {it.content && <p className="text-muted-foreground mt-1 leading-relaxed">{it.content}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (s.type === "goals") {
    return (
      <div>
        <SectionHeading>{s.title}</SectionHeading>
        <div className="grid sm:grid-cols-2 gap-2">
          {items.map((it, i) => (
            <div key={it.id || i} className="rounded-2xl bg-surface p-4 sm:p-5">
              <span className="text-[13px] font-bold text-primary tabular-nums">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="font-bold text-[16px] mt-1.5 leading-snug">{it.title}</h3>
              {it.content && <p className="text-[13.5px] text-muted-foreground mt-1 leading-relaxed">{it.content}</p>}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (s.type === "process") {
    return (
      <div>
        <SectionHeading>{s.title}</SectionHeading>
        <ol className="relative space-y-6 before:absolute before:left-[13px] before:top-3 before:bottom-3 before:w-px before:bg-black/10">
          {items.map((it, i) => (
            <li key={it.id || i} className="relative flex gap-3.5">
              <span className="relative z-10 w-7 h-7 shrink-0 rounded-full border-[1.5px] border-[#CFC9BE] bg-white text-[13px] font-bold flex items-center justify-center">{i + 1}</span>
              <div className="pt-0.5 min-w-0">
                <h3 className="font-bold text-[16px] leading-snug">{it.title}</h3>
                {it.content && <p className="text-muted-foreground mt-1 leading-relaxed">{it.content}</p>}
              </div>
            </li>
          ))}
        </ol>
      </div>
    );
  }

  if (s.type === "stats" && !premium) {
    return (
      <div>
        <SectionHeading>{s.title}</SectionHeading>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
          {items.map((it, i) => (
            <div key={it.id || i} className="border border-border rounded-sm p-4 sm:p-6 text-center bg-surface">
              <p className="font-heading text-3xl sm:text-4xl font-bold text-primary">{it.name}</p>
              <p className="text-sm text-muted-foreground mt-2">{it.description}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (s.type === "stats") {
    return (
      <div>
        <SectionHeading>{s.title}</SectionHeading>
        <div className={`grid grid-cols-2 ${STAT_COLS[Math.min(items.length, 4)] || "sm:grid-cols-3"} gap-2`}>
          {items.map((it, i) => (
            <div key={it.id || i} className="rounded-2xl bg-surface p-4 sm:p-5 text-center">
              <p className="font-display font-extrabold text-[2rem] sm:text-4xl leading-none tracking-[-0.02em]">{it.name}</p>
              <p className="text-[13px] text-muted-foreground mt-2 leading-snug">{it.description}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (s.type === "team") {
    return (
      <div>
        <SectionHeading>{s.title}</SectionHeading>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {items.map((it, i) => (
            <div key={it.id || i} className="flex items-center gap-4 border border-border rounded-sm p-5">
              {it.image
                ? <img src={safeImgSrc(it.image)} alt={it.name} className="w-16 h-16 rounded-sm object-cover shrink-0" />
                : <div className="w-16 h-16 rounded-sm bg-primary/10 text-primary flex items-center justify-center font-heading font-bold text-xl shrink-0">{(it.name || "?").charAt(0)}</div>}
              <div>
                <p className="font-heading font-semibold">{it.name}</p>
                <p className="text-sm text-muted-foreground">{it.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (s.type === "testimonials") {
    return (
      <div>
        <SectionHeading>{s.title}</SectionHeading>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {items.map((it, i) => (
            <figure key={it.id || i} className="border border-border rounded-sm p-5 sm:p-6 bg-surface">
              <blockquote className="text-foreground/90 leading-relaxed">“{it.content}”</blockquote>
              <figcaption className="flex items-center gap-3 mt-5">
                {it.image
                  ? <img src={safeImgSrc(it.image)} alt={it.name} className="w-10 h-10 rounded-full object-cover" />
                  : <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm">{(it.name || "?").charAt(0)}</div>}
                <div>
                  <p className="text-sm font-semibold">{it.name}</p>
                  <p className="text-xs text-muted-foreground">{it.description}</p>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    );
  }

  if (s.type === "faq" && !premium) {
    return (
      <div>
        <SectionHeading>{s.title}</SectionHeading>
        <div className="divide-y divide-border border-t border-border">
          {items.map((it, i) => (
            <div key={it.id || i} className="py-4">
              <p className="font-heading font-semibold">{it.title}</p>
              {it.content && <p className="text-muted-foreground mt-1.5 leading-relaxed">{it.content}</p>}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (s.type === "faq") {
    return (
      <div>
        <SectionHeading>{s.title}</SectionHeading>
        <div className="divide-y divide-[#EFEBE4]">
          {items.map((it, i) => (
            <details key={it.id || i} className="group py-3.5 first:pt-0">
              <summary className="flex items-center justify-between gap-4 cursor-pointer list-none font-bold text-[15.5px] [&::-webkit-details-marker]:hidden">
                {it.title}
                <span className="w-8 h-8 shrink-0 rounded-full bg-surface flex items-center justify-center text-lg leading-none transition-transform group-open:rotate-45" aria-hidden="true">+</span>
              </summary>
              {it.content && <p className="text-muted-foreground mt-2 leading-relaxed pr-10">{it.content}</p>}
            </details>
          ))}
        </div>
      </div>
    );
  }

  if (s.type === "image") {
    return (
      <figure>
        {s.content
          ? <img src={safeImgSrc(s.content)} alt={s.title || ""} className="w-full rounded-sm border border-border object-cover" />
          : <div className="w-full h-48 rounded-sm border border-dashed border-border flex items-center justify-center text-muted-foreground text-sm">Image</div>}
        {s.title && <figcaption className="text-sm text-muted-foreground mt-2 text-center">{s.title}</figcaption>}
      </figure>
    );
  }

  if (s.type === "video") {
    const embed = videoEmbedUrl(s.content);
    return (
      <figure data-testid="video-section">
        {!s.content ? (
          <div className="w-full h-48 rounded-sm border border-dashed border-border flex items-center justify-center text-muted-foreground text-sm">{tr("Vidéo", "Video")}</div>
        ) : embed ? (
          <div className="relative w-full rounded-sm overflow-hidden border border-border" style={{ paddingTop: "56.25%" }}>
            <iframe title={s.title || tr("Vidéo", "Video")} src={embed} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen className="absolute inset-0 w-full h-full" />
          </div>
        ) : (
          <video src={safeImgSrc(s.content)} controls className="w-full rounded-sm border border-border" />
        )}
        {s.title && <figcaption className="text-sm text-muted-foreground mt-2 text-center">{s.title}</figcaption>}
      </figure>
    );
  }

  if (s.type === "diagram") {
    return <DiagramSection s={s} brand={brand} />;
  }

  // Premium : la grille dessine sa propre carte — une ligne par service, bord à bord : nom (× quantité), description,
  // pastilles (engagement, prestataire tiers, remise), prix net à droite (brut barré si remise), puis le total.
  if (s.type === "pricing" && premium) {
    const { oneShot, recurring } = sectionAmounts(s);
    const chip = "px-2 py-0.5 rounded-full bg-surface text-[11.5px] font-semibold text-foreground/80";
    return (
      <div className="rounded-[20px] bg-white overflow-hidden">
        <div className="px-5 sm:px-8 pt-5 sm:pt-7">
          <SectionHeading>{s.title}</SectionHeading>
          {s.image && <ZoomableImage src={s.image} alt={s.title} className="mb-4" />}
        </div>
        {items.map((it, i) => {
          const qty = Number(it.quantity) || 0;
          const gross = qty * (Number(it.unit_price) || 0);
          const net = lineAmount(it);
          const hasDiscount = !!it.discount_type && net < gross;
          const monthly = it.recurrence === "mensuel";
          const commitment = monthly && it.commitment_months != null;
          return (
            <div key={it.id || i} className="flex gap-4 items-start px-5 sm:px-8 py-4 border-t border-[#EFEBE4]">
              <div className="min-w-0 flex-1">
                <p className="font-bold text-[15px] leading-snug">
                  {it.name}{qty > 1 && <span className="font-semibold text-muted-foreground"> × {qty}</span>}
                </p>
                {it.description && <p className="text-[13px] text-muted-foreground mt-1 leading-snug">{it.description}</p>}
                {(commitment || it.provider?.name || hasDiscount) && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {commitment && (
                      <span className={chip} data-testid={`commitment-${it.id || i}`}>
                        {commitmentText(it)}
                      </span>
                    )}
                    {it.provider?.name && <span className={chip}>{tr("Assuré par", "Provided by")} {it.provider.name}</span>}
                    {hasDiscount && (
                      <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 text-[11.5px] font-bold">{fmtDiscount(it.discount_type, it.discount_value, currency)}</span>
                    )}
                  </div>
                )}
              </div>
              <div className="text-right shrink-0">
                {hasDiscount && <p className="text-xs text-muted-foreground line-through tabular-nums">{fmtMoney(gross, currency)}</p>}
                <p className="font-display font-bold text-lg leading-tight tabular-nums">{fmtMoney(net, currency)}</p>
                <p className="text-[11.5px] text-muted-foreground whitespace-nowrap">{monthly ? words(HT, tr("/ mois", "/ month")) : HT ? `${HT}, ${tr("une fois", "one-off")}` : tr("une fois", "one-off")}</p>
              </div>
            </div>
          );
        })}
        {items.length > 0 && (
          <div className="px-5 sm:px-8 py-4 bg-surface text-sm space-y-1.5">
            {!!s.discount_type && (
              <>
                <div className="flex justify-between text-muted-foreground"><span>{words(tr("Sous-total", "Subtotal"), HT)}</span><span className="tabular-nums">{fmtMoney(items.reduce((a, it) => a + lineAmount(it), 0), currency)}</span></div>
                <div className="flex justify-between text-emerald-800 font-semibold"><span>{tr("Remise globale", "Overall discount")}</span><span className="tabular-nums">{fmtDiscount(s.discount_type, s.discount_value, currency)}</span></div>
              </>
            )}
            {recurring > 0 && <div className="flex justify-between font-bold"><span>{words("Total", HT)} — {tr("par mois", "per month")}</span><span className="tabular-nums">{fmtMoney(recurring, currency)}{tr("/mois", "/month")}</span></div>}
            {oneShot > 0 && <div className="flex justify-between font-bold"><span>{words("Total", HT)}{recurring > 0 ? tr(" — une fois", " — one-off") : ""}</span><span className="tabular-nums">{fmtMoney(oneShot, currency)}</span></div>}
          </div>
        )}
      </div>
    );
  }

  if (s.type === "pricing") {
    return (
      <div>
        <SectionHeading>{s.title}</SectionHeading>
        {s.image && <ZoomableImage src={s.image} alt={s.title} className="mb-5" />}
        <div className="border border-border rounded-sm overflow-hidden">
          {items.map((it, i) => {
            const gross = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
            const net = lineAmount(it);
            const hasDiscount = !!it.discount_type && net < gross;
            return (
              <div key={it.id || i} className={`flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between px-5 py-4 ${i % 2 ? "bg-surface" : "bg-white"}`}>
                <div className="min-w-0">
                  <p className="font-medium text-sm">{it.name}</p>
                  {it.description && <p className="text-xs text-muted-foreground">{it.description}</p>}
                </div>
                <div className="sm:text-right sm:shrink-0 sm:ml-4">
                  <p className="font-mono text-sm font-semibold">
                    {hasDiscount && <span className="line-through text-muted-foreground font-normal mr-2">{fmtMoney(gross, currency)}</span>}
                    {fmtMoney(net, currency)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {it.quantity} × {words(fmtMoney(it.unit_price, currency), HT)}{it.recurrence === "mensuel" ? tr(" /mois", " /month") : ""}
                    {it.recurrence === "mensuel" && it.commitment_months != null && (
                      <span className="ml-1.5" data-testid={`commitment-${it.id || i}`}>
                        · {commitmentText(it)}
                      </span>
                    )}
                    {hasDiscount && <span className="ml-1.5 text-emerald-700 font-medium whitespace-nowrap">{fmtDiscount(it.discount_type, it.discount_value, currency)}</span>}
                  </p>
                </div>
              </div>
            );
          })}
          {/* Récapitulatif TOUJOURS affiché (une grille tarifaire se termine par son total) ;
              les lignes Sous-total/Remise n'apparaissent qu'en présence d'une remise globale.
              Section mixte (mensuel + unique) : les deux totaux affichés séparément, jamais
              additionnés — un total unique mélangerait un abonnement et des frais ponctuels. */}
          {items.length > 0 && (() => {
            const subtotal = items.reduce((a, it) => a + lineAmount(it), 0);
            const net = applyDiscount(subtotal, s.discount_type, s.discount_value);
            const hasSectionDiscount = !!s.discount_type;
            const { oneShot, recurring } = sectionAmounts(s);
            const mixed = oneShot > 0 && recurring > 0;
            return (
              <div className="px-5 py-3 bg-surface border-t border-border text-sm space-y-1">
                {hasSectionDiscount && (
                  <>
                    <div className="flex justify-between text-muted-foreground"><span>{words(tr("Sous-total", "Subtotal"), HT)}</span><span className="font-mono">{fmtMoney(subtotal, currency)}</span></div>
                    <div className="flex justify-between text-emerald-700 font-medium"><span>{tr("Remise globale", "Overall discount")}</span><span className="font-mono">{fmtDiscount(s.discount_type, s.discount_value, currency)}</span></div>
                  </>
                )}
                {mixed ? (
                  <>
                    <div className="flex justify-between font-semibold"><span>{words("Total", HT)} — {tr("une fois", "one-off")}</span><span className="font-mono">{fmtMoney(oneShot, currency)}</span></div>
                    <div className="flex justify-between font-semibold"><span>{words("Total", HT)} — {tr("par mois", "per month")}</span><span className="font-mono">{fmtMoney(recurring, currency)}{tr("/mois", "/month")}</span></div>
                  </>
                ) : (
                  <div className="flex justify-between font-semibold">
                    <span>{words("Total", HT)}{recurring > 0 ? tr(" — par mois", " — per month") : ""}</span>
                    <span className="font-mono">{fmtMoney(net, currency)}{recurring > 0 ? tr("/mois", "/month") : ""}</span>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    );
  }

  // Type de section non reconnu : repli gracieux — afficher le contenu texte
  // s'il existe (jamais un tableau de prix erroné), sinon ne rien rendre.
  if (s.content) {
    return (
      <div>
        <SectionHeading>{s.title}</SectionHeading>
        <p className="text-foreground/80 leading-relaxed whitespace-pre-line text-[15px]">{s.content}</p>
      </div>
    );
  }
  return null;
}

export function DiagramSection({ s, brand }) {
  const accent = brand?.accent_hex || '#6366f1';

  // Sécurité de rendu : un `subtype` manquant/invalide ne doit jamais rendre la section
  // silencieusement invisible (« section fantôme ») — la normalisation serveur garantit un
  // subtype valide à l'écriture, mais cette donnée peut provenir d'un état antérieur au
  // correctif. On déduit un rendu au mieux à partir de la forme des items plutôt que de
  // renvoyer `null` sans indice.
  let subtype = s.subtype;
  if (subtype !== 'process' && subtype !== 'timeline' && subtype !== 'comparison') {
    const isCompShape = s.items && typeof s.items === 'object' && !Array.isArray(s.items);
    subtype = isCompShape ? 'comparison' : 'process';
  }

  if (subtype === 'process') {
    const items = Array.isArray(s.items) ? s.items : [];
    // Rien d'exploitable : ne pas afficher un titre suivi d'un bloc vide (incohérent pour
    // le prospect) — choix délibéré de ne rien rendre plutôt qu'un état vide trompeur.
    if (items.length === 0) return null;
    return (
      <div>
        <h2 className="text-xl font-bold font-heading mb-6">{s.title}</h2>
        <div className="flex flex-wrap gap-3">
          {items.map((item, idx) => (
            <div key={item.id || idx} className="flex-1 min-w-[160px] sm:max-w-[220px] rounded-sm border border-border p-4 relative">
              <span className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold mb-3"
                style={{ backgroundColor: accent }}>
                {item.step || idx + 1}
              </span>
              <p className="text-sm font-semibold leading-tight mb-1">{item.title}</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{item.description}</p>
              {idx < items.length - 1 && (
                <span className="hidden md:block absolute -right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-lg">›</span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (subtype === 'timeline') {
    const items = Array.isArray(s.items) ? s.items : [];
    if (items.length === 0) return null;
    return (
      <div>
        <h2 className="text-xl font-bold font-heading mb-6">{s.title}</h2>
        <div className="relative pl-6 space-y-6">
          <div className="absolute left-2.5 top-1 bottom-1 w-px bg-border" />
          {items.map((item, idx) => (
            <div key={item.id || idx} className="relative">
              <span className="absolute -left-6 w-5 h-5 rounded-full border-2 flex items-center justify-center"
                style={{
                  backgroundColor: item.milestone ? accent : 'white',
                  borderColor: accent,
                }}>
                {item.milestone && <span className="w-2 h-2 rounded-full bg-white" />}
              </span>
              <p className="text-xs font-semibold uppercase tracking-wider mb-0.5" style={{ color: accent }}>{item.date_label}</p>
              <p className="text-sm font-semibold">{item.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (subtype === 'comparison') {
    const data = s.items || {};
    const headers = Array.isArray(data.headers) ? data.headers : [];
    const rows = Array.isArray(data.rows) ? data.rows : [];
    if (headers.length === 0 && rows.length === 0) return null;
    return (
      <div>
        <h2 className="text-xl font-bold font-heading mb-6">{s.title}</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className="text-left py-2 pr-4 text-muted-foreground font-medium w-1/3"></th>
                {headers.map((h, i) => (
                  <th key={i} className="py-2 px-3 text-center font-semibold rounded-t-sm"
                    style={i === 0 ? { backgroundColor: accent + '18', color: accent } : {}}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} className="border-t border-border">
                  <td className="py-2 pr-4 text-muted-foreground text-xs">{row.feature}</td>
                  {(row.values || []).map((val, vi) => (
                    <td key={vi} className="py-2 px-3 text-center"
                      style={vi === 0 ? { color: accent, fontWeight: 600 } : {}}>
                      {val === true || val === 'true' ? '✓' : val === false || val === 'false' ? '—' : val}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return null;
}
